import { Ref, ref, computed } from "vue";
import {
  Metadata,
  MetadataKeys,
  User,
  UserType,
  VignetteHelpers,
} from "@/types";
import { useSessionStore } from "@/pinia/session";
import { LogService } from "@/services";
import { usePageTitle } from "@/composition/usePageTitle";

export function useViewAnalytics(pageName: Ref<string>) {
  const sessionStore = useSessionStore();
  const sessionInfo = computed<User>(() => sessionStore.user);
  const { pageTitle } = usePageTitle(pageName);

  const metadata = ref<Metadata>({
    // User metadata
    [MetadataKeys.LOGIN_STATUS]:
      sessionInfo.value != null ? "Logged In" : "Anonymous",
    [MetadataKeys.AUTHENTICATION_TYPE]:
      sessionInfo.value != null
        ? sessionInfo.value?.userType === UserType.WEB_USER
          ? "Individual Web User"
          : "IP Authentication"
        : "(Not Logged In)",
    [MetadataKeys.USER_OR_IP_RANGE_SALESFORCE_ID]:
      sessionInfo.value != null
        ? sessionInfo.value?.salesforceId
        : "(Not Logged In)",
    [MetadataKeys.USER_ORGANIZATION_SALESFORCE_ID]:
      sessionInfo.value != null
        ? sessionInfo.value?.accountId
        : "(Not Logged In)",
    [MetadataKeys.USER_PERMISSIONS]:
      sessionInfo.value != null ? "vignette-subscriber" : "(Not Logged In)",

    // Page metadata
    [MetadataKeys.CONTENT_TITLE]: pageName.value.trim() || "Vignette",
    [MetadataKeys.CMS_CONTENT_ID]: undefined,
    [MetadataKeys.SITESECTION_CHANNEL]: "Vignette",
    [MetadataKeys.CMS_TAGS]: undefined,
    [MetadataKeys.PAYWALLED_CONTENT_STATUS]: "Behind the Paywall",
    [MetadataKeys.PAGE_TYPE]: "(Unknown)",
    [MetadataKeys.SITESECTION]: "(Unknown)",
    [MetadataKeys.VIGNETTE_PRIMARY_PROFILE_TYPE]: undefined,
  });

  VignetteHelpers.Analytics.push(metadata.value);

  const setAnalyticsMetadata = (key: MetadataKeys, value: string): void => {
    LogService.info(`ViewAnalytics - updating metadata for key: '${key}'`);
    metadata.value[key] = value;

    VignetteHelpers.Analytics.push({
      [key]: value,
    });
  };

  const clearAnalyticsMetadata = (key: MetadataKeys): void => {
    LogService.info(`ViewAnalytics - clearing metadata for key: '${key}'`);
    metadata.value[key] = undefined;

    VignetteHelpers.Analytics.push({
      [key]: undefined,
    });
  };

  const pushPageViewEvent = (): void => {
    LogService.info("ViewAnalytics - sending page view event beacon");
    VignetteHelpers.Analytics.pushEvent("page-view-ready");
  };

  return {
    pageTitle,
    metadata,
    setAnalyticsMetadata,
    clearAnalyticsMetadata,
    pushPageViewEvent,
  };
}
